// Thin adapter over @intrasync/react's injectStores.
//
// The library implementation is behaviourally equivalent to the one this file
// used to contain: same /\w*Store\b/g source scrape, same excludeStoreNames
// (syncRootStore / loginSyncRootStore), same uniqueArray de-dupe, same plain
// observer(Comp) fallback when no store is found, and the same
// "names.join('-')" injector displayName.
//
// Two deliberate differences:
//   1. It injects through mobx-react's function-mapper form instead of
//      inject(...names). The mapper reads rootStore[key] LIVE at render and
//      returns undefined for a missing store, where grabStoresByName threw
//      "MobX injector: Store 'x' is not available!". That matters here because
//      RootStoreProvider memoizes its {rootStore, ...rootStore} snapshot on
//      first render, which happens BEFORE RootStore.deferredStoreInitialization
//      mounts signaturePadStore/appVersionStore/authRedirectStore/... in its
//      setTimeout - those keys are absent from the frozen snapshot forever.
//   2. Its built-in allowlist is a superset of ./stores/builtInStores except
//      for `projectStore`, so we register the local list verbatim below.
//      setupInjectStores takes appStore .name values without transformation,
//      which is what makes this exact.
import type React from "react";

import {
  injectStores as libInjectStores,
  setupInjectStores as libSetupInjectStores,
  type InjectStoresOptions,
  type WithInjectedStores,
} from "@intrasync/react";

import { builtInStores } from "../../stores";

interface Store {
  name: string;
}

/**
 * Adds the names of included stores to the shared @intrasync/react registry.
 *
 * NOTE - REPLACE SEMANTICS: the library's setupInjectStores rebuilds its entire
 * allowlist on every call and console.warns about names a later call drops.
 * Call this exactly once, at bootstrap, and do NOT also call
 * @intrasync/react's setupInjectStores directly (see src/main.tsx).
 *
 * @param {Object[]} appStores - The array of app stores.
 * @param {Object[]} serviceStores - The array of service stores.
 * @returns {Promise<boolean>} - Resolves once the allowlist has been built.
 */
export const setupInjectStores = (
  appStores: Store[],
  serviceStores: Store[]
): Promise<boolean> =>
  libSetupInjectStores(
    [...appStores, ...builtInStores.map((name: string) => ({ name }))],
    serviceStores
  );

/**
 * injectStores - Wraps Component with observer and injects any store used in
 * the export.
 *
 * The library ships two overloads: a TYPED one that requires an `options`
 * argument, and a LEGACY untyped one that is NOT generic. Eight files under
 * src/views call it as `injectStores<SomeProps>(Component)` — a type argument
 * with a single value argument — which matches neither: supplying a type
 * argument rules out the non-generic legacy overload, and the typed overload
 * then reports "Expected 2 arguments, but got 1".
 *
 * src/views is off limits to edit, so the missing overload is declared here.
 * It is purely a typing concern: the underlying runtime function already takes
 * `options` optionally, so passing one argument was always valid at runtime.
 */
function injectStores<P extends object>(
  Comp: React.ComponentType<P>,
  options: InjectStoresOptions
): React.ComponentType<WithInjectedStores<P>>;
function injectStores<P extends object>(Comp: React.ComponentType<P>): any;
function injectStores(Comp: any): any;
function injectStores(Comp: any, options?: InjectStoresOptions): any {
  return options === undefined
    ? (libInjectStores as (c: any) => any)(Comp)
    : (libInjectStores as (c: any, o: InjectStoresOptions) => any)(Comp, options);
}

export default injectStores;
